import React, { FC, useEffect } from 'react';

import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';
import * as yup from 'yup';

import { Button, Stack, TextField } from '@mui/material';

import {
  CreateCategoryResponse,
  useGetPaginateCategory,
  useUpdateCategory,
} from '../../api-http/categories';
import { AlertBar } from '../../components/AlertBar';
import { formMsg, notifyMsg } from '../../configs';
import { ERROR, SUCCESS } from '../../util/constant';
import { useSort } from '../../util/useSortHook';
import { PAGE, ROWS_PER_PAGE } from '../constant';

interface CategoryRenameFormProps {
  onFinished: () => void;
  category: CreateCategoryResponse | undefined;
}

const validationSchema = yup.object({
  title: yup.string().required(formMsg.TITLE_REQUIRED),
});

const Container = styled.div`
  display: flex;
  padding-top: 18px;
  padding-bottom: 18px;
  flex-direction: column;
`;

export const RenameForm: FC<CategoryRenameFormProps> = ({
  onFinished,
  category,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, error, data, mutate, isSuccess } = useUpdateCategory();
  const { sortBy, order } = useSort();

  const { refetch } = useGetPaginateCategory({
    limit: ROWS_PER_PAGE,
    page: PAGE + 1,
    sortby: sortBy,
    order,
  });

  useEffect(() => {
    if (data) {
      onFinished();
    }
  }, [data]);

  const formik = useFormik({
    initialValues: {
      title: category?.name || '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      mutate({
        _id: category?._id || '',
        name: values?.title || '',
      });
    },
  });
  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar(notifyMsg.CATEGORY_RENAME_SUCCESS, {
        variant: SUCCESS,
      });
    }
    if (error) {
      enqueueSnackbar(notifyMsg.CATEGORY_RENAME_FAILED, {
        variant: ERROR,
      });
    }
    refetch();
  }, [isSuccess, error]);

  return (
    <Container>
      <AlertBar severity="error" msg={error?.msg} />

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            id="title"
            name="title"
            label="Category name"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
          />

          <Button
            color="primary"
            disabled={!formik.dirty || isLoading}
            variant="contained"
            fullWidth
            type="submit"
            sx={{ minHeight: 40 }}
          >
            Update
          </Button>
        </Stack>
      </form>
    </Container>
  );
};
